import {useRef, useState} from 'react'
import {AnchoredOverlay, Button, IconButton, useResponsiveValue} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {StackIcon, StackRemoveIcon, XIcon} from '@primer/octicons-react'
import {getBaseBranchText} from '../utils/branch-label'
import {FeatureState, type StackData} from '../page-data/payloads/header'
import {useUnstackPullRequestsMutation} from '../mutations/use-unstack-pull-requests-mutation'
import {useQueryClient} from '@github-ui/react-query'
import {useHeaderPageDataQueryKey} from '../page-data/loaders/use-header-page-data'
import {useLocation} from '@github-ui/react-router'
import {getPullRequestTabSuffix} from '@github-ui/paths'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {UnstackDialog} from './UnstackButtonDialog'
import {StackOverlay} from './StackOverlay'
import styles from './StackState.module.css'
import {useFeatureState} from '../hooks/use-feature-state'
import {buildPullRequestUserActionContext} from '../analytics/pull-request-user-action'

type StackStateProps = {
  pullRequestNumber: number
  stack: StackData | undefined | null
}

export function StackState({pullRequestNumber, stack}: StackStateProps) {
  const isPullRequestStacksEnabled = useFeatureState('pullRequestStacks') === FeatureState.Enabled
  const isPreventUnstackingLockedPrsEnabled =
    useFeatureState('pullRequestStacksPreventUnstackingLockedPrs') === FeatureState.Enabled
  const [open, setOpen] = useState<boolean>(false)
  const [isConfirmingUnstack, setIsConfirmingUnstack] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState('')
  const unstackButtonRef = useRef<HTMLButtonElement>(null)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const queryClient = useQueryClient()
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const headerPageDataQueryKey = useHeaderPageDataQueryKey()
  const {mutate: mutateUnstackPullRequests, isPending: isLoading} = useUnstackPullRequestsMutation()
  const {pathname} = useLocation()
  const isNarrow = useResponsiveValue({narrow: true}, false)

  if (!isPullRequestStacksEnabled) {
    return null
  }

  if (!stack) {
    return null
  }

  const {baseBranch, repositoryOwnerLogin, repositoryName, position, size, pulls, allPullsLocked} = stack
  const stackAnalytics = {
    id: stack.id,
    number: stack.number,
    created_at: stack.createdAt,
  }
  const baseBranchText = getBaseBranchText(baseBranch, repositoryOwnerLogin, repositoryOwnerLogin)

  // Pulls array is ordered from head to base, so top pull in the stack is the first item in the array.
  const topPullInStack = pulls[0]
  const addToStackBaseBranch = topPullInStack?.headBranch ?? baseBranch
  const addToStackHeadBranch = addToStackBaseBranch // Use same branch for now, until we have a "smarter" way of determining head ref for new PR
  const addToStackURL = `/${repositoryOwnerLogin}/${repositoryName}/compare/${addToStackBaseBranch}...${addToStackHeadBranch}?stack=true`

  // Preserve the current tab (e.g. /changes, /commits) when navigating between stacked PRs
  const tabSuffix = getPullRequestTabSuffix(pathname)

  // Hide unstack only when the flag is enabled and every pull in the stack is locked.
  const shouldHideUnstackButton = isPreventUnstackingLockedPrsEnabled && allPullsLocked

  const someLockedPulls = pulls.some(pull => !pull.isUnstackable)

  // Determine isInactive based on feature flag logic and legacy behavior
  let isDialogInactive = false
  if (!isPreventUnstackingLockedPrsEnabled && someLockedPulls) {
    isDialogInactive = true
  }

  const stackNavContent = (
    <StackOverlay
      pullRequestNumber={pullRequestNumber}
      stack={stack}
      baseBranchText={baseBranchText}
      addToStackURL={addToStackURL}
      tabSuffix={tabSuffix}
      onSwitchBranch={(targetPullRequestId, targetPositionInStack) => {
        sendClickAnalyticsEvent(
          buildPullRequestUserActionContext({
            category: 'stack_navigator',
            action: 'switch_branch',
            data: {
              stack: stackAnalytics,
              target_pull_request_id: targetPullRequestId,
              target_position_in_stack: targetPositionInStack,
            },
          }),
        )
      }}
      onAddToStackClick={() => {
        sendClickAnalyticsEvent(
          buildPullRequestUserActionContext({
            category: 'stack_navigator',
            action: 'add_to_stack_click',
            data: {
              stack: stackAnalytics,
              stack_size_before: size,
            },
          }),
        )
      }}
    />
  )

  return (
    <div className="d-flex flex-items-center fgColor-muted">
      <Button
        ref={anchorRef}
        className={styles.stackBadge}
        variant="invisible"
        leadingVisual={StackIcon}
        aria-label={`Navigate pull request stack, position ${position} of ${size}`}
        aria-expanded={open}
        aria-haspopup={isNarrow ? 'dialog' : 'true'}
        onClick={() => {
          setOpen(previousOpen => {
            if (!previousOpen) {
              sendClickAnalyticsEvent(
                buildPullRequestUserActionContext({
                  category: 'stack_navigator',
                  action: 'open',
                  data: {
                    stack: stackAnalytics,
                    source: 'pr_header',
                  },
                }),
              )
            }

            return !previousOpen
          })
        }}
      >
        {position}/{size}
      </Button>
      {isNarrow ? (
        <>
          {open && (
            <Dialog
              renderHeader={({dialogLabelId, onClose: closeDialog}) => (
                <div className={styles.dialogHeader}>
                  <Dialog.Title id={dialogLabelId}>Stack #{stack.number}</Dialog.Title>
                  <div className={styles.overlayHeaderActions}>
                    {!shouldHideUnstackButton && (
                      <IconButton
                        ref={unstackButtonRef}
                        icon={StackRemoveIcon}
                        variant="invisible"
                        size="small"
                        aria-label="Unstack"
                        onClick={() => {
                          sendClickAnalyticsEvent(
                            buildPullRequestUserActionContext({
                              category: 'stack_navigator',
                              action: 'unstack_click',
                              data: {
                                stack: stackAnalytics,
                                stack_size: size,
                              },
                            }),
                          )
                          setIsConfirmingUnstack(true)
                        }}
                      />
                    )}
                    <IconButton
                      icon={XIcon}
                      variant="invisible"
                      size="small"
                      aria-label="Close"
                      onClick={() => closeDialog('close-button')}
                    />
                  </div>
                </div>
              )}
              onClose={() => setOpen(false)}
              position={{narrow: 'bottom', regular: 'center'}}
              height="auto"
              renderBody={({children}) => <Dialog.Body className="px-1 pt-1 pb-2">{children}</Dialog.Body>}
            >
              {stackNavContent}
            </Dialog>
          )}
        </>
      ) : (
        <AnchoredOverlay
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          renderAnchor={null}
          anchorRef={anchorRef}
        >
          <div>
            <div className={styles.overlayHeader}>
              <span className={styles.overlayHeaderTitle}>Stack #{stack.number}</span>
              <div className={styles.overlayHeaderActions}>
                {!shouldHideUnstackButton && (
                  <IconButton
                    ref={unstackButtonRef}
                    icon={StackRemoveIcon}
                    variant="invisible"
                    size="small"
                    aria-label="Unstack"
                    onClick={() => {
                      sendClickAnalyticsEvent(
                        buildPullRequestUserActionContext({
                          category: 'stack_navigator',
                          action: 'unstack_click',
                          data: {
                            stack: stackAnalytics,
                            stack_size: size,
                          },
                        }),
                      )
                      setIsConfirmingUnstack(true)
                    }}
                  />
                )}
                <IconButton
                  icon={XIcon}
                  variant="invisible"
                  size="small"
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                />
              </div>
            </div>
            {stackNavContent}
          </div>
        </AnchoredOverlay>
      )}
      <UnstackDialog
        unstackButtonRef={unstackButtonRef}
        isConfirmingUnstack={isConfirmingUnstack}
        isInactive={isDialogInactive}
        isLoading={isLoading}
        errorMessage={errorMessage}
        someLockedPulls={someLockedPulls}
        onCancelUnstack={() => {
          setIsConfirmingUnstack(false)
          setErrorMessage('')
        }}
        onConfirmUnstack={() => {
          setErrorMessage('')
          mutateUnstackPullRequests(
            {stackId: stack.id},
            {
              onError: error => setErrorMessage(error.message),
              onSuccess: async () => {
                try {
                  await queryClient.refetchQueries({queryKey: headerPageDataQueryKey}, {throwOnError: true})
                  setIsConfirmingUnstack(false)
                  setOpen(false)
                } catch {
                  setErrorMessage('Something unexpected happened. Please refresh the page.')
                }
              },
            },
          )
        }}
      />
    </div>
  )
}
